// src/components/Auth/RecruiterSignupModal.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recruiterSignUp } from '../../services/recruiter';

const RecruiterSignupModal = ({ isOpen, onClose, onSwitchToLogin, onSwitchToJobSeeker }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isAgency, setIsAgency] = useState(false);
    
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        businessType: '',
        firstName: '',
        lastName: '',
        contactNumber: '',
        companyName: '',
        registrationNumber: '',
        companyEmail: '',
        province: '',
        city: '',
        website: '',
        linkedin: '',
        idNumber: '',
        address: '',
    });

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validateStep1 = () => {
        if (!formData.email) {
            setError('Email is required');
            return false;
        }
        if (!formData.password || formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return false;
        }
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }
        setError('');
        return true;
    };

    const validateStep2 = () => {
        if (!formData.businessType) {
            setError('Please select a business type');
            return false;
        }
        setError('');
        return true;
    };

    const validateStep3 = () => {
        if (!formData.firstName || !formData.lastName || !formData.contactNumber) {
            setError('Please fill in all representative details');
            return false;
        }
        
        if (formData.businessType === 'company' || formData.businessType === 'agency') {
            if (!formData.companyName || !formData.registrationNumber || !formData.companyEmail) {
                setError('Please fill in all company details');
                return false;
            }
        }
        
        if (formData.businessType === 'sole') {
            if (!formData.idNumber || !formData.address) {
                setError('Please fill in all required fields');
                return false;
            }
        }
        
        setError('');
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep3()) return;
        
        setLoading(true);
        setError('');

        let userData = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            contactNumber: formData.contactNumber,
            businessType: formData.businessType,
            isAgency: isAgency,
        };

        if (formData.businessType === 'company' || formData.businessType === 'agency') {
            userData = {
                ...userData,
                companyName: formData.companyName,
                registrationNumber: formData.registrationNumber,
                companyEmail: formData.companyEmail,
                province: formData.province,
                city: formData.city,
                website: formData.website || '',
                linkedin: formData.linkedin || '',
            };
        }

        if (formData.businessType === 'sole') {
            userData = {
                ...userData,
                idNumber: formData.idNumber,
                address: formData.address,
                website: formData.website || '',
                linkedin: formData.linkedin || '',
            };
        }

        const result = await recruiterSignUp(formData.email, formData.password, userData);
        
        if (result.success) {
            onClose();
            navigate('/recruiter-pending');
        } else {
            // Friendly error messages
            const errorMsg = result.error;
            if (errorMsg.includes('Email already registered')) {
                setError('This email is already registered. Please login instead.');
            } else if (errorMsg.includes('Password should be at least 6 characters')) {
                setError('Password must be at least 6 characters long.');
            } else if (errorMsg.includes('Too many requests')) {
                setError('Too many requests. Please try again later.');
            } else if (errorMsg.includes('Network error')) {
                setError('Network error. Please check your internet connection.');
            } else {
                setError(errorMsg || 'Sign up failed. Please try again.');
            }
        }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative animate-fadeIn" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 text-2xl z-10">
                    <i className="fas fa-times"></i>
                </button>

                <div className="p-6">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-full flex items-center justify-center mx-auto mb-3">
                            <i className="fas fa-building text-white text-2xl"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-gradpath-dark">Recruiter Sign Up</h2>
                        <p className="text-gray-500 text-sm">Create your recruiter account</p>
                    </div>

                    {/* Progress Bar */}
                    <div className="flex justify-between mb-2">
                        <span className={`text-xs ${step >= 1 ? 'text-gradpath-green font-medium' : 'text-gray-400'}`}>Account</span>
                        <span className={`text-xs ${step >= 2 ? 'text-gradpath-green font-medium' : 'text-gray-400'}`}>Business</span>
                        <span className={`text-xs ${step >= 3 ? 'text-gradpath-green font-medium' : 'text-gray-400'}`}>Details</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                        <div className="bg-gradpath-green h-2 rounded-full transition-all duration-500" style={{ width: `${(step / 3) * 100}%` }}></div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {error && (
                            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        {/* Step 1: Account */}
                        {step === 1 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Email Address *"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Password <span className="text-red-500">*</span>
                                        <span className="text-xs text-gray-400 ml-1">(min 6 characters)</span>
                                    </label>
                                    <input
                                        type="password"
                                        name="password"
                                        placeholder="Password * (min 6 characters)"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Confirm Password <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        placeholder="Confirm Password *"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {/* Step 2: Business Type */}
                        {step === 2 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Business Type <span className="text-red-500">*</span>
                                    </label>
                                    <div className="grid grid-cols-3 gap-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, businessType: 'company' }));
                                                setIsAgency(false);
                                            }}
                                            className={`p-4 rounded-lg border-2 transition-all ${
                                                formData.businessType === 'company'
                                                    ? 'border-gradpath-green bg-green-50'
                                                    : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                        >
                                            <i className="fas fa-building text-2xl block mb-2"></i>
                                            <span className="text-sm font-medium">Company</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, businessType: 'agency' }));
                                                setIsAgency(true);
                                            }}
                                            className={`p-4 rounded-lg border-2 transition-all ${
                                                formData.businessType === 'agency'
                                                    ? 'border-gradpath-green bg-green-50'
                                                    : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                        >
                                            <i className="fas fa-handshake text-2xl block mb-2"></i>
                                            <span className="text-sm font-medium">Agency</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, businessType: 'sole' }));
                                                setIsAgency(false);
                                            }}
                                            className={`p-4 rounded-lg border-2 transition-all ${
                                                formData.businessType === 'sole'
                                                    ? 'border-gradpath-green bg-green-50'
                                                    : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                        >
                                            <i className="fas fa-user-tie text-2xl block mb-2"></i>
                                            <span className="text-sm font-medium">Sole Proprietor</span>
                                        </button>
                                    </div>
                                </div>

                                {formData.businessType === 'agency' && (
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={isAgency}
                                                onChange={(e) => setIsAgency(e.target.checked)}
                                                className="w-4 h-4 text-gradpath-green rounded"
                                            />
                                            <span className="text-sm text-gray-700">
                                                We are a recruitment agency recruiting on behalf of a client
                                            </span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 3: Details */}
                        {step === 3 && (
                            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                                <h3 className="font-bold text-gradpath-dark">Representative Details</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            First Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            placeholder="First Name *"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Surname <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            placeholder="Surname *"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contact Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="contactNumber"
                                        placeholder="Contact Number *"
                                        value={formData.contactNumber}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                        required
                                    />
                                </div>

                                {/* Company/Agency Details */}
                                {(formData.businessType === 'company' || formData.businessType === 'agency') && (
                                    <>
                                        <h3 className="font-bold text-gradpath-dark pt-2">Company Details</h3>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Company Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="companyName"
                                                placeholder="Company Name *"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Registration Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="registrationNumber"
                                                placeholder="Registration Number *"
                                                value={formData.registrationNumber}
                                                onChange={handleChange}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Company Email <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="companyEmail"
                                                placeholder="Company Email *"
                                                value={formData.companyEmail}
                                                onChange={handleChange}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                                required
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Province <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    name="province"
                                                    value={formData.province}
                                                    onChange={handleChange}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                                    required
                                                >
                                                    <option value="">Province *</option>
                                                    <option value="Eastern Cape">Eastern Cape</option>
                                                    <option value="Free State">Free State</option>
                                                    <option value="Gauteng">Gauteng</option>
                                                    <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                                                    <option value="Limpopo">Limpopo</option>
                                                    <option value="Mpumalanga">Mpumalanga</option>
                                                    <option value="Northern Cape">Northern Cape</option>
                                                    <option value="North West">North West</option>
                                                    <option value="Western Cape">Western Cape</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    City <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    placeholder="City *"
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Company Website
                                                <span className="text-xs text-gray-400 ml-1">(Optional)</span>
                                            </label>
                                            <input
                                                type="url"
                                                name="website"
                                                placeholder="Company Website (Optional)"
                                                value={formData.website}
                                                onChange={handleChange}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                LinkedIn Profile
                                                <span className="text-xs text-gray-400 ml-1">(Optional)</span>
                                            </label>
                                            <input
                                                type="url"
                                                name="linkedin"
                                                placeholder="LinkedIn Profile (Optional)"
                                                value={formData.linkedin}
                                                onChange={handleChange}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Sole Proprietor Details */}
                                {formData.businessType === 'sole' && (
                                    <>
                                        <h3 className="font-bold text-gradpath-dark pt-2">Personal Details</h3>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                ID Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="idNumber"
                                                placeholder="ID Number *"
                                                value={formData.idNumber}
                                                onChange={handleChange}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Home/Business Address <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="address"
                                                placeholder="Home/Business Address *"
                                                value={formData.address}
                                                onChange={handleChange}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Website/LinkedIn Profile
                                                <span className="text-xs text-gray-400 ml-1">(Optional)</span>
                                            </label>
                                            <input
                                                type="url"
                                                name="website"
                                                placeholder="Website/LinkedIn (Optional)"
                                                value={formData.website}
                                                onChange={handleChange}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        <div className="flex gap-4 mt-6 pt-4 border-t border-gray-200">
                            {step > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setStep(step - 1)}
                                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                                >
                                    Back
                                </button>
                            )}
                            {step < 3 ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (step === 1 && !validateStep1()) return;
                                        if (step === 2 && !validateStep2()) return;
                                        setStep(step + 1);
                                    }}
                                    className="flex-1 bg-gradpath-green text-white px-6 py-2 rounded-lg hover:bg-gradpath-dark transition"
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 bg-gradpath-green text-white px-6 py-2 rounded-lg hover:bg-gradpath-dark transition disabled:opacity-50"
                                >
                                    {loading ? <i className="fas fa-spinner fa-spin"></i> : 'Submit Application'}
                                </button>
                            )}
                        </div>
                    </form>

                    <div className="text-center mt-6 pt-4 border-t border-gray-200 space-y-2">
                        <p className="text-sm text-gray-600">
                            Already have a recruiter account?{' '}
                            <button onClick={onSwitchToLogin} className="text-gradpath-green font-semibold hover:underline">
                                Login here
                            </button>
                        </p>
                        <p className="text-sm text-gray-600">
                            Are you a job seeker?{' '}
                            <button onClick={onSwitchToJobSeeker} className="text-gradpath-green font-semibold hover:underline">
                                Sign up as Job Seeker
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecruiterSignupModal;